import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Check, Shield, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionsPage = () => {
  const { user, token, logout } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);

  useEffect(() => {
    fetchSubscriptionFeatures();
  }, []);

  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscription/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription features:', error);
    }
  };

  const currentPlan = subscriptionFeatures?.subscription_plan || 'standard';

  const plans = [
    {
      id: 'standard',
      name: 'Standard',
      price: '0',
      period: '/month',
      description: 'Your complete Paify system',
      features: [
        'No setup fee',
        '3% platform fee on transactions',
        'We only earn when you do',
        'Structured checkout (4 options)',
        'Smart contracts',
        'All-in-one emailing',
        'Hosted link',
        'Provider dashboard',
        'Mobile-friendly'
      ],
      buttonText: currentPlan === 'standard' ? 'Current Plan' : 'Switch to Standard',
      buttonClass: currentPlan === 'standard' 
        ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
        : 'bg-black hover:bg-gray-800 text-white',
      popular: false,
      disabled: currentPlan === 'standard'
    },
    {
      id: 'paidd_plus',
      name: 'Paify Plus',
      price: '19.99',
      period: '/month',
      description: 'Everything in Standard, plus professional features',
      features: [
        '2% platform fee (save 1%)',
        'Client subscriptions',
        'Brand & logo uploads',
        'VAT receipts',
        'Reminders & dunning',
        'Invoices & Quotes',
        'Late-payment interest',
        'Certified Paify badge'
      ],
      buttonText: currentPlan === 'paidd_plus' 
        ? 'Current Plan' 
        : currentPlan === 'embedded' ? 'Switch to Paify Plus' : 'Upgrade to Paify Plus',
      buttonClass: currentPlan === 'paidd_plus' 
        ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
        : 'bg-[#e6007e] hover:bg-[#c4006b] text-white',
      popular: true,
      disabled: currentPlan === 'paidd_plus'
    },
    {
      id: 'embedded',
      name: 'Embedded',
      price: '29.99',
      period: '/month',
      description: 'Everything in Standard and Paify Plus, plus full white-label solution',
      features: [
        '2% platform fee',
        'Full checkout system embedded on your website',
        'Client subscriptions',
        'Widget fee £199',
        'Website integration fee £49 (optional)'
      ],
      buttonText: currentPlan === 'embedded' ? 'Current Plan' : 'Upgrade to Embedded',
      buttonClass: currentPlan === 'embedded'
        ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
        : 'bg-black hover:bg-gray-800 text-white',
      popular: false,
      disabled: currentPlan === 'embedded'
    }
  ];

  const addOns = [
    {
      id: 'vat_vault',
      name: 'VAT Vault',
      price: '9.99',
      period: '/month',
      description: '',
      features: ['Auto-allocate % to VAT pot', 'Monthly summary & export'],
      available: true
    },
    {
      id: 'invoice_insurance',
      name: 'Invoice Insurance',
      price: '',
      period: '',
      description: '',
      features: ['Partner-backed coverage', 'Project risk assessment', 'Claim support service', 'Peace of mind guarantee'],
      available: false,
      comingSoon: true
    }
  ];

  const handleSubscribe = async (planId) => {
    if (loading) return;
    setLoading(true);

    try {
      if (planId === 'standard') {
        const response = await axios.post(`${API}/subscription/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          toast({
            title: "Subscription Updated",
            description: response.data.immediate 
              ? "You've been downgraded to the Standard plan."
              : response.data.message,
          });
          setTimeout(() => { fetchSubscriptionFeatures(); }, 1000);
        }
        setLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to process subscription:', error);
      toast({
        title: "Subscription Error",
        description: error.response?.data?.detail || "There was an issue processing your request. Please try again.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleAddOnSubscribe = async (addOnId) => {
    if (addOnId === 'invoice_insurance') {
      toast({ title: "Coming Soon", description: "Invoice Insurance will be available soon. We'll notify you when it's ready!" });
      return;
    }

    const userAddOns = subscriptionFeatures?.add_ons || [];
    if (userAddOns.includes(addOnId)) {
      toast({ title: "Already Subscribed", description: "You already have access to this add-on." });
      return;
    }

    setLoading(true);
    toast({ title: "Add-on Selected", description: "Redirecting to secure checkout for VAT Vault add-on..." });

    try {
      const response = await axios.post(`${API}/add-ons/vat-vault/subscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create add-on checkout:', error);
      toast({
        title: "Subscription Error",
        description: error.response?.data?.detail || "There was an issue processing your request. Please try again.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="pricing" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 font-heading">
            Every business deserves to get <em>paid on time</em> — <strong>without</strong> chasing.
          </h1>
          <h2 className="text-2xl font-semibold text-gray-700 font-heading">
            One tool. Clear pricing.
          </h2>
        </div>

        {subscriptionFeatures?.subscription_status === 'cancelling' && subscriptionFeatures?.subscription_expires_at && (
          <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Subscription Ending</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Your {subscriptionFeatures.plan_info.name} subscription will end on{' '}
                    {new Date(subscriptionFeatures.subscription_expires_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}. After this date, you'll be on the Standard plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative border-0 shadow-lg ${plan.popular ? 'ring-2 ring-[#e6007e]' : ''}`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-[#e6007e] text-white px-4 py-1">Most Popular</Badge>
                </div>
              )}
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 font-heading">{plan.name}</h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-4xl font-bold text-gray-900">£{plan.price}</span>
                    <span className="text-gray-500 font-body">{plan.period}</span>
                  </div>
                  <p className="text-gray-600 font-body">{plan.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={plan.disabled || loading}
                  className={`w-full py-3 font-body ${plan.buttonClass}`}
                >
                  {loading ? "Processing..." : plan.buttonText}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">Add-ons</h2>
            <p className="text-gray-600 font-body">Enhance your subscription with powerful extras</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {addOns.map((addOn) => (
              <Card key={addOn.id} className="border-0 shadow-md relative">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading">{addOn.name}</h3>
                    {addOn.comingSoon && <p className="text-sm text-gray-500 font-body mb-2">Coming Soon</p>}
                    <div className="flex items-baseline justify-center mb-2">
                      {addOn.available && addOn.price ? (
                        <>
                          <span className="text-2xl font-bold text-gray-900">£{addOn.price}</span>
                          <span className="text-gray-500 font-body">{addOn.period}</span>
                        </>
                      ) : null}
                    </div>
                    <p className="text-gray-600 font-body text-sm">{addOn.description}</p>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {addOn.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-body text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {(() => {
                    const userAddOns = subscriptionFeatures?.add_ons || [];
                    const hasAddOn = userAddOns.includes(addOn.id);
                    const isDisabled = !addOn.available || hasAddOn || loading;
                    return (
                      <Button
                        onClick={() => handleAddOnSubscribe(addOn.id)}
                        disabled={isDisabled}
                        data-testid={`addon-${addOn.id}-btn`}
                        className={`w-full font-body ${
                          hasAddOn ? 'bg-green-100 text-green-800 cursor-not-allowed'
                            : addOn.available ? 'bg-[#e6007e] hover:bg-[#c4006b] text-white'
                              : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {hasAddOn ? 'Added to Plan' : 'Add to Plan'}
                      </Button>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-[#e6007e] mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 font-heading">Trust Badge</h3>
          </div>
          <p className="text-gray-600 font-body mb-4">
            Display the Paify Certified badge on your website to signal structure and fair terms to clients.
          </p>
          <Badge className="bg-blue-100 text-blue-800">Included with subscription</Badge>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500 font-body text-sm">
            All plans include payment processing via Stripe, SSL encryption, and 24/7 uptime monitoring.
            <br />
            Cancel anytime. No long-term contracts.
          </p>
        </div>
      </main>
    </div>
  );
};

export const SubscriptionSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyPayment = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sessionId = searchParams.get('session_id');
      if (!sessionId) { setError('No session ID found'); setLoading(false); return; }
      try {
        const response = await axios.get(`${API}/subscription/payment-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });
        setPaymentStatus(response.data);
        setLoading(false);
        setTimeout(() => { window.location.href = '/dashboard'; }, 3000);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Failed to verify payment. Please contact support.');
        setLoading(false);
      }
    };
    verifyPayment();
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto mb-4"></div>
          <p className="text-gray-600 font-body">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Payment Verification Failed</h2>
          <p className="text-gray-600 mb-6 font-body">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#e6007e] hover:bg-[#c4006b]">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Subscription Upgraded!</h2>
        <p className="text-gray-600 mb-2 font-body">Your subscription has been successfully upgraded to <strong>{paymentStatus?.subscription_plan?.replace('_', ' ').toUpperCase()}</strong></p>
        <p className="text-gray-500 text-sm mb-6 font-body">Redirecting to dashboard...</p>
        <Button onClick={() => navigate('/dashboard')} className="bg-[#e6007e] hover:bg-[#c4006b]">Go to Dashboard Now</Button>
      </div>
    </div>
  );
};

export const SubscriptionCancel = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Upgrade Cancelled</h2>
        <p className="text-gray-600 mb-6 font-body">Your subscription upgrade was cancelled. No charges have been made.</p>
        <Button onClick={() => navigate('/dashboard')} className="bg-[#e6007e] hover:bg-[#c4006b]">Return to Dashboard</Button>
      </div>
    </div>
  );
};

export const SubscriptionGatedPage = ({ component: Component, requiredPlan, featureName }) => {
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();

  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscription/features`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptionFeatures(response.data);
      const hasAccess = requiredPlan === 'paidd_plus' ? response.data.features?.quotes || response.data.features?.invoices || response.data.features?.branding_logo : true;
      if (!hasAccess) { setShowUpgradeModal(true); }
    } catch (error) { console.error('Failed to fetch subscription features:', error); }
    finally { setLoading(false); }
  };

  const handleUpgradeSubscription = async (planType) => {
    try {
      const response = await axios.post(`${API}/subscription/create-checkout-session`, { plan_id: planType, origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.checkout_url) { window.location.href = response.data.checkout_url; }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({ title: "Error", description: "Failed to start upgrade process. Please try again.", variant: "destructive" });
    }
  };

  useEffect(() => { if (token) { fetchSubscriptionFeatures(); } }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  const hasAccess = requiredPlan === 'paidd_plus' ? subscriptionFeatures?.features?.quotes || subscriptionFeatures?.features?.invoices || subscriptionFeatures?.features?.branding_logo : true;

  if (!hasAccess || showUpgradeModal) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <Crown className="w-16 h-16 text-[#e6007e] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Upgrade Required</h1>
            <p className="text-gray-700 mb-6 font-body">{featureName} are available with <strong>Paify Plus</strong> subscription or higher.</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Crown className="w-4 h-4 text-yellow-600" />
                <span className="font-medium text-yellow-800 font-body">Paify Plus Features</span>
              </div>
              <ul className="text-sm text-yellow-700 space-y-1 font-body">
                <li>Create professional quotes</li>
                <li>Send invoices to clients</li>
                <li>Add your branding & logo</li>
                <li>VAT receipts & reporting</li>
                <li>Trust badge verification</li>
              </ul>
            </div>
            <div className="flex justify-center space-x-4">
              <Button variant="outline" onClick={() => window.history.back()} className="font-body">Go Back</Button>
              <Button onClick={() => handleUpgradeSubscription('paidd_plus')} className="bg-[#e6007e] hover:bg-pink-700 text-white font-body">
                {subscriptionFeatures?.subscription_plan === 'embedded' ? 'Switch to Paify Plus' : 'Upgrade to Paify Plus'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <Component />;
};
